import type { Room } from "@catmail/shared";
import { Menu } from "lucide-react";
import type { ReactNode } from "react";

import { Avatar, IconButton } from "@/components";
import { FOCUS, PRESS_FLAT } from "@/lib/interaction";

import {
  avatarToneOf,
  peersOf,
  roomLeadOf,
  roomSubtitleOf,
  roomTitleOf,
} from "../../lib/roomView";

/** 그룹방 헤더에 겹쳐 보이는 아바타 수 */
const STACK_LIMIT = 3;

type ChatHeaderProps = {
  room: Room;
  /** 프로필을 여는 건 이름 영역을 눌렀을 때 */
  onOpenProfile: () => void;
  /** 지금 이 방에서 오가는 제목. 마지막 메시지의 제목이다. */
  subject?: string;
  /** 오른쪽에 붙는 것 — 첨부/더보기, 또는 "대화 목록으로 옮기기" */
  actions?: ReactNode;
  /**
   * 방 목록 서랍을 연다. **좁은 화면에서만 보인다.**
   *
   * 거기서는 목록이 서랍 안에 있어서 평소에는 화면에 없다. 가장자리를 쓸어도
   * 열리지만, 그걸 아는 사람만 쓸 수 있는 길로 두면 안 된다.
   */
  onOpenNav?: () => void;
};

export function ChatHeader({
  room,
  subject,
  onOpenProfile,
  actions,
  onOpenNav,
}: ChatHeaderProps) {
  const title = roomTitleOf(room);
  const subtitle = roomSubtitleOf(room);
  const lead = roomLeadOf(room);
  // 나도 방의 일원이므로 스택 맨 뒤에 끼워 넣는다 (시안 05: 박·김·예)
  const stack = [...peersOf(room), ...room.participants.filter((p) => p.isSelf)]
    .slice(0, STACK_LIMIT);

  return (
    <header className="border-line flex h-18 shrink-0 items-center gap-1.5 border-b px-3 sm:gap-3 sm:px-6.5">
      {onOpenNav ? (
        <IconButton label="방 목록 열기" onClick={onOpenNav} className="-ml-1 md:hidden">
          <Menu />
        </IconButton>
      ) : null}

      <button
        type="button"
        onClick={onOpenProfile}
        className={`hover:bg-paper/60 -mx-2 flex min-w-0 items-center gap-3 rounded-xl px-2 py-1 text-left ${PRESS_FLAT} ${FOCUS}`}
      >
        {room.kind === "group" ? (
          <span className="flex shrink-0">
            {stack.map((peer, index) => (
              <Avatar
                key={peer.id}
                size="lg"
                initials={peer.initials}
                colorIndex={peer.colorIndex}
                imageUrl={peer.avatarUrl}
                label={peer.displayName}
                className={
                  index === 0 ? undefined : "border-paper -ml-3 border-2"
                }
              />
            ))}
          </span>
        ) : (
          <Avatar
            size="lg"
            initials={lead?.initials ?? "?"}
            colorIndex={lead?.colorIndex ?? 0}
            imageUrl={lead?.avatarUrl}
            dotTone={lead ? avatarToneOf(lead) : null}
            label={title}
          />
        )}

        <span className="flex min-w-0 flex-col gap-px">
          <span className="text-ink line-clamp-1-ko text-base font-semibold">
            {title}
            {room.kind === "group" ? (
              <span className="text-muted-2 ml-1.5 font-medium">
                {room.participants.length}
              </span>
            ) : null}
          </span>
          {/*
            메일에는 제목이 있는데 채팅 화면에는 놓을 자리가 없다. 말풍선마다
            붙이면 시끄럽고, 안 보이면 "지금 무슨 건으로 이야기 중인지"를 알 수
            없다 — 상대 메일함에는 이게 제목으로 떠 있는데 나만 못 보는 셈이다.
            그래서 지금 제목을 여기 한 줄로 둔다. 바뀌는 순간은 대화 안의
            구분선이 따로 알려준다.
          */}
          <span className="text-muted-2 line-clamp-1-ko text-xs">
            {subject ? subject : subtitle}
          </span>
        </span>
      </button>

      {actions ? (
        <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div>
      ) : null}
    </header>
  );
}
